import {
  Avatar,
  AvatarFallback,
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  confirm,
  Dialog,
  DialogContent,
  DialogPortal,
  DialogTitle,
  Textarea,
  toast,
} from "@devhop/ui";
import { useNavigate, useParams } from "@tanstack/react-router";
import { format } from "date-fns";
import {
  ArrowLeftIcon,
  BuildingIcon,
  CalendarDaysIcon,
  CalendarIcon,
  CheckCircleIcon,
  ClipboardIcon,
  EditIcon,
  GraduationCapIcon,
  InfoIcon,
  MapPinIcon,
  PackageIcon,
  UserCheckIcon,
  UserXIcon,
  ClockIcon,
  XIcon,
} from "lucide-react";
import { useEffect, useState } from "react";
import {
  isMultiMemberPackageKind,
  multiMemberBookingTitle,
} from "@/modules/private-lessons/domain/lib/package-kind";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { useActionPermission } from "@/modules/auth/presentation/model/useActionPermission";
import { formatDate, formatDateTime, formatTime } from "@/shared/lib/date-time";
import { formatSlotHours } from "@/shared/lib/private-lesson-slot-duration";
import { getInitials } from "@/shared/lib/utils";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import { QueryState } from "@/shared/ui/QueryState";
import {
  useCancelPrivateLessonBooking,
  useCheckInPrivateLessonBooking,
  usePrivateLessonBookingAttendanceQuery,
  usePrivateLessonBookingQuery,
  useUpdatePrivateLessonBooking,
} from "../api/queries";
import { CheckInPrivateForm } from "@/modules/attendance/presentation/ui/CheckInPrivateForm";
import { usePatchAttendanceRecordTimeMutation } from "@/modules/attendance/presentation/api/queries";
import {
  EditCheckInTimeDialog,
  type EditCheckInTimeTarget,
} from "@/modules/attendance/presentation/ui/EditCheckInTimeDialog";
import { PrivateLessonBookingLocationCell } from "../ui/PrivateLessonBookingLocationCell";
import { PrivateLessonGroupPackageBadge } from "../ui/PrivateLessonGroupPackageBadge";
import { PrivateLessonInstructorAttendanceCard } from "../ui/PrivateLessonInstructorAttendanceCard";

export function PrivateLessonBookingDetailPage() {
  const nav = useNavigate({
    from: "/app/private-lessons/bookings/$id",
  });
  const { id } = useParams({
    from: "/app/private-lessons/bookings/$id",
  });
  const { data: booking, ...result } = usePrivateLessonBookingQuery(id);
  const { data: attendanceData } = usePrivateLessonBookingAttendanceQuery(id);
  const attendances = attendanceData?.records ?? [];
  const canUpdate = useActionPermission(["private-lessons:update"]);
  const cancelBooking = useCancelPrivateLessonBooking();
  const updateBooking = useUpdatePrivateLessonBooking();
  const checkInBooking = useCheckInPrivateLessonBooking();
  const patchTimeMutation = usePatchAttendanceRecordTimeMutation({
    onSuccess: () => {
      void result.refetch();
    },
  });
  const [adminNotes, setAdminNotes] = useState(booking?.notes || "");
  const [checkInOpen, setCheckInOpen] = useState(false);
  const [editTimeOpen, setEditTimeOpen] = useState(false);
  const [editTimeTarget, setEditTimeTarget] =
    useState<EditCheckInTimeTarget | null>(null);
  const isMultiMemberBooking = isMultiMemberPackageKind(booking?.packageKind);

  // Update admin notes when booking data changes
  useEffect(() => {
    if (booking?.notes !== undefined) {
      setAdminNotes(booking.notes || "");
    }
  }, [booking?.notes]);

  const studentCheckInBlocked =
    booking?.engagementKind === "partner_instructor_only";
  const canOpenCheckIn =
    canUpdate &&
    booking != null &&
    (booking.status === "completed" ||
      (booking.status === "scheduled" && !studentCheckInBlocked));
  const isCompletedInstructorCheckIn = booking?.status === "completed";
  const bookingIdShort = booking?.id.slice(0, 8).toUpperCase() || "";
  const bookingRef = `PL-${bookingIdShort}-${format(new Date(booking?.createdAt || new Date()), "yy")}`;

  const statusLabels = {
    scheduled: "ຈອງແລ້ວ",
    completed: "ສຳເລັດ",
    cancelled: "ຍົກເລີກ",
  };

  const statusVariants = {
    scheduled: "default" as const,
    completed: "secondary" as const,
    cancelled: "outline" as const,
  };

  const handleSaveNotes = async () => {
    if (!booking) return;
    try {
      await updateBooking.mutateAsync({
        id: booking.id,
        input: {
          date: booking.date,
          startTime: booking.startTime,
          endTime: booking.endTime,
          locationKind: booking.locationKind,
          offSiteLabel: booking.offSiteLabel,
          engagementKind: booking.engagementKind,
          roomId: booking.roomId ?? undefined,
          instructorIds: booking.instructorIds ?? [],
          notes: adminNotes || undefined,
        },
      });
      toast.success("ບັນທຶກໝາຍເຫດສຳເລັດ");
    } catch {
      // Error handling is done by the mutation
    }
  };

  const usedHours = booking
    ? booking.package.totalHours - booking.package.remainingHours
    : 0;
  const totalHours = booking?.package.totalHours || 0;
  const progressPercentage =
    totalHours > 0 ? (usedHours / totalHours) * 100 : 0;

  return (
    <>
      <Header>
        <Breadcrumb
          items={[
            { label: "ແຜງຄວບຄຸມ", to: "/app/dashboard" },
            { label: "ການຈອງຮຽນສ່ວນຕົວ", to: "/app/private-lessons/bookings" },
            { label: "ລາຍລະອຽດ" },
          ]}
        />
      </Header>

      <Main>
        <QueryState
          result={result}
          title="ກຳລັງໂຫຼດການຈອງ"
          description="ກຳລັງດຶງລາຍລະອຽດ"
          variant="fullscreen"
        >
          {!booking ? null : (
            <div className="space-y-6">
              {/* Header */}
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3">
                    <h1 className="font-bold text-3xl tracking-tight">
                      {multiMemberBookingTitle(booking.packageKind)}: #BK-
                      {bookingIdShort}
                    </h1>
                    <PrivateLessonGroupPackageBadge
                      packageKind={booking.packageKind}
                      groupSize={booking.groupSize}
                    />
                    <Badge
                      variant={statusVariants[booking.status]}
                      className="rounded-full"
                    >
                      {statusLabels[booking.status]}
                    </Badge>
                  </div>
                  <p className="mt-1 text-muted-foreground">
                    ສ້າງເມື່ອ {formatDate(booking.createdAt)} • ອ້າງອີງ: {bookingRef}
                  </p>
                </div>
                <div className="flex gap-2">
                  {canOpenCheckIn && (
                    <Button
                      className="bg-red-600 hover:bg-red-700"
                      onClick={() => setCheckInOpen(true)}
                      disabled={checkInBooking.isPending}
                    >
                      <CheckCircleIcon className="size-4" />
                      {checkInBooking.isPending
                        ? "ກຳລັງກວດສອບເຂົ້າ..."
                        : isCompletedInstructorCheckIn
                          ? "ກວດຊື່ຄູສອນ"
                          : "ກວດສອບເຂົ້າ"}
                    </Button>
                  )}
                  {booking.status === "scheduled" && canUpdate && (
                    <>
                      <Button
                        variant="outline"
                        onClick={() =>
                          nav({
                            to: "/app/private-lessons/bookings/$id/edit",
                            params: { id },
                          })
                        }
                      >
                        <EditIcon className="size-4" />
                        ແກ້ໄຂ
                      </Button>
                      <Button
                        variant="outline"
                        className="text-red-600 hover:text-red-700"
                        onClick={async () => {
                          const ok = await confirm({
                            title: "ຍົກເລີກການຈອງ",
                            description: "ທ່ານແນ່ໃຈບໍ່ວ່າຈະຍົກເລີກການຈອງນີ້?",
                            actionText: "ຍົກເລີກ",
                            ActionProps: {
                              variant: "destructive",
                            },
                          });
                          if (ok) {
                            toast.promise(cancelBooking.mutateAsync(id), {
                              loading: "ກຳລັງຍົກເລີກ...",
                              success: "ຍົກເລີກການຈອງສຳເລັດ",
                              error: "ຍົກເລີກການຈອງລົ້ມເຫຼວ",
                            });
                          }
                        }}
                      >
                        <XIcon className="size-4" />
                        ຍົກເລີກການຮຽນ
                      </Button>
                    </>
                  )}
                  <Button
                    variant="outline"
                    onClick={() => nav({ to: "/app/private-lessons/bookings" })}
                  >
                    <ArrowLeftIcon className="size-4" />
                    ກັບຄືນ
                  </Button>
                </div>
              </div>

              {/* 2-Column Layout */}
              <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
                {/* Left Column */}
                <div className="col-span-2 space-y-6">
                  {/* Booking Information Card */}
                  <Card>
                    <CardHeader>
                      <div className="flex items-center gap-2">
                        <InfoIcon className="size-5 text-red-600" />
                        <CardTitle>ຂໍ້ມູນການຈອງ</CardTitle>
                      </div>
                    </CardHeader>
                    <CardContent className="grid grid-cols-1 gap-6 lg:grid-cols-2">
                      {/* Student Details */}
                      <div>
                        <div className="mb-2 font-medium text-muted-foreground text-xs uppercase">
                          ນັກຮຽນ
                        </div>
                        <div className="flex items-center gap-3">
                          <Avatar className="h-12 w-12">
                            <AvatarFallback className="bg-red-100 text-red-600">
                              {getInitials(booking.studentName)}
                            </AvatarFallback>
                          </Avatar>
                          <div>
                            <p className="font-semibold">
                              {booking.studentName}
                            </p>
                            <p className="text-muted-foreground text-sm">
                              {booking.studentMemberId}
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Schedule Details */}
                      <div>
                        <div className="mb-2 font-medium text-muted-foreground text-xs uppercase">
                          ຕາຕະລາງ
                        </div>
                        <div className="flex items-start gap-2">
                          <CalendarIcon className="mt-1 size-4 text-red-600" />
                          <div>
                            <p className="font-semibold">
                              {formatDate(booking.date)}
                            </p>
                            <p className="text-muted-foreground text-sm">
                              {formatTime(booking.startTime)} -{" "}
                              {formatTime(booking.endTime)} (
                              {booking.durationMinutes} ນາທີ)
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Location Details */}
                      <div>
                        <div className="mb-2 font-medium text-muted-foreground text-xs uppercase">
                          ສະຖານທີ່
                        </div>
                        <div className="flex items-start gap-2">
                          {booking.locationKind === "off_site" ? (
                            <MapPinIcon className="mt-1 size-4 text-red-600" />
                          ) : (
                            <BuildingIcon className="mt-1 size-4 text-red-600" />
                          )}
                          <div>
                            <PrivateLessonBookingLocationCell
                              locationKind={booking.locationKind}
                              offSiteLabel={booking.offSiteLabel}
                              offSiteDistance={booking.offSiteDistance}
                              roomName={booking.roomName}
                            />
                            {booking.locationKind === "off_site" ? (
                              <p className="mt-1 text-muted-foreground text-sm">
                                ຈອງເປັນເວລາຄູ — ບໍ່ໃຊ້ຫ້ອງ
                              </p>
                            ) : null}
                          </div>
                        </div>
                      </div>

                      {/* Instructor Details */}
                      <div>
                        <div className="mb-2 font-medium text-muted-foreground text-xs uppercase">
                          ຄູຜູ້ສອນ
                        </div>
                        <div className="flex items-start gap-2">
                          <GraduationCapIcon className="mt-1 size-4 text-red-600" />
                          <div>
                            <p className="font-semibold">
                              {(booking.instructorNames ?? []).join(", ") ||
                                "-"}
                            </p>
                            <p className="text-muted-foreground text-sm">-</p>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Attendance Record Card */}
                  <Card>
                    <CardHeader>
                      <div className="flex items-center gap-2">
                        <ClipboardIcon className="size-5 text-red-600" />
                        <CardTitle>ບັນທຶກການເຂົ້າຮ່ວມ</CardTitle>
                      </div>
                    </CardHeader>
                    <CardContent>
                      {attendances.length > 0 ? (
                        <div className="space-y-3">
                          {isMultiMemberBooking && (
                            <p className="text-muted-foreground text-sm">
                              ກວດຊື່ແລ້ວ {attendances.length}/
                              {booking.members?.length ?? booking.groupSize ?? "?"}{" "}
                              ຄົນ
                            </p>
                          )}
                          {attendances.map((a) => (
                            <div
                              key={a.id}
                              className="flex items-center gap-3 rounded-lg border border-green-500/30 bg-green-500/5 p-3"
                            >
                              <UserCheckIcon className="size-5 shrink-0 text-green-600" />
                              <div className="min-w-0 flex-1">
                                <p className="font-semibold">{a.studentName}</p>
                                <p className="text-muted-foreground text-sm">
                                  {formatDateTime(a.checkedInAt)}
                                </p>
                              </div>
                              {canUpdate && booking.status !== "cancelled" && (
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  className="shrink-0 gap-1.5"
                                  disabled={patchTimeMutation.isPending}
                                  onClick={() => {
                                    setEditTimeTarget({
                                      id: a.id,
                                      label: a.studentName,
                                      checkedInAt: a.checkedInAt,
                                    });
                                    setEditTimeOpen(true);
                                  }}
                                >
                                  <ClockIcon className="size-3.5" />
                                  ເວລາ
                                </Button>
                              )}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="flex flex-col items-center justify-center py-8 text-center">
                          <div className="mb-4 flex size-16 items-center justify-center rounded-full bg-muted">
                            <UserXIcon className="size-8 text-muted-foreground" />
                          </div>
                          <p className="mb-2 font-semibold">ຍັງບໍ່ມີການເຂົ້າຮ່ວມ</p>
                          <p className="mb-4 text-muted-foreground text-sm">
                            {isMultiMemberBooking
                              ? "ເລືອກສະມາຊິກທີ່ມາຮຽນໃນຟອມກວດຊື່"
                              : "ກົດ 'ກວດສອບເຂົ້າ' ເພື່ອບັນທຶກການມາຮອດ"}
                          </p>
                          {canOpenCheckIn && (
                            <Button
                              variant="outline"
                              className="bg-red-50 text-red-600 hover:bg-red-100"
                              onClick={() => setCheckInOpen(true)}
                              disabled={checkInBooking.isPending}
                            >
                              {isCompletedInstructorCheckIn
                                ? "ກວດຊື່ຄູສອນ"
                                : isMultiMemberBooking
                                  ? "ກວດຊື່ສະມາຊິກ"
                                  : "ກວດສອບເຂົ້ານັກຮຽນ"}
                            </Button>
                          )}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </div>

                {/* Right Column */}
                <div className="space-y-6">
                  <PrivateLessonInstructorAttendanceCard
                    bookingId={id}
                    instructorIds={booking.instructorIds}
                    instructorNames={booking.instructorNames}
                    status={booking.status}
                    canCorrect={canUpdate}
                    onCorrected={() => result.refetch()}
                  />

                  {/* Package Details Card */}
                  <Card>
                    <CardHeader>
                      <div className="flex items-center gap-2">
                        <PackageIcon className="size-5 text-red-600" />
                        <CardTitle>ລາຍລະອຽດແພັກເກດ</CardTitle>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-6">
                      <div>
                        <div className="mb-2 font-medium text-muted-foreground text-xs uppercase">
                          ຊື່ແພັກເກດ
                        </div>
                        <p className="flex flex-wrap items-center gap-2 font-semibold">
                          {booking.package.templateName}
                          <PrivateLessonGroupPackageBadge
                            packageKind={booking.package.packageKind}
                            groupSize={booking.package.groupSize}
                          />
                        </p>
                      </div>

                      {isMultiMemberBooking && booking.members && booking.members.length > 0 && (
                        <div>
                          <div className="mb-2 font-medium text-muted-foreground text-xs uppercase">
                            ສະມາຊິກໃນກຸ່ມ
                          </div>
                          <ul className="space-y-1 text-sm">
                            {booking.members.map((m) => (
                              <li key={m.studentId}>
                                {m.studentName}{" "}
                                <span className="text-muted-foreground">
                                  ({m.studentMemberId})
                                </span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {/* Hours Consumption */}
                      <div>
                        <div className="mb-2 font-medium text-muted-foreground text-xs uppercase">
                          ການໃຊ້ຊົ່ວໂມງ
                        </div>
                        <div className="space-y-2">
                          <div className="flex justify-between text-sm">
                            <span className="text-muted-foreground">
                              {formatSlotHours(usedHours)} / {formatSlotHours(totalHours)} ຊົ່ວໂມງ
                            </span>
                            <span className="font-semibold">
                              {progressPercentage.toFixed(0)}%
                            </span>
                          </div>
                          <div className="h-2 w-full overflow-hidden rounded-full bg-muted">
                            <div
                              className="h-full bg-red-600 transition-all"
                              style={{ width: `${progressPercentage}%` }}
                            />
                          </div>
                        </div>
                      </div>

                      <div>
                        <div className="mb-2 font-medium text-muted-foreground text-xs uppercase">
                          ຊົ່ວໂມງທີເຫຼືອ
                        </div>
                        <p className="font-semibold text-lg text-red-600">
                          {formatSlotHours(booking.package.remainingHours)} ຊົ່ວໂມງ
                        </p>
                      </div>

                      <div>
                        <div className="mb-2 font-medium text-muted-foreground text-xs uppercase">
                          ວັນທີໝົດອາຍຸ
                        </div>
                        <div className="flex items-center gap-2">
                          <CalendarDaysIcon className="size-4 text-red-600" />
                          <p className="font-semibold">
                            {formatDate(booking.package.expireDate)}
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Admin Notes Card */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="uppercase">
                        ໝາຍເຫດຜູ້ດູແລລະບົບ
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <Textarea
                        placeholder="ເພີ່ມໝາຍເຫດສຳລັບການຈອງນີ້..."
                        value={adminNotes}
                        onChange={(e) => setAdminNotes(e.target.value)}
                        rows={6}
                        className="resize-none"
                        disabled={booking?.status !== "scheduled"}
                      />
                      <Button
                        onClick={handleSaveNotes}
                        disabled={
                          updateBooking.isPending ||
                          booking?.status !== "scheduled"
                        }
                        className="w-full"
                      >
                        {booking?.status !== "scheduled"
                          ? "ບໍ່ສາມາດບັນທຶກໝາຍເຫດໄດ້"
                          : updateBooking.isPending
                            ? "ກຳລັງບັນທຶກ..."
                            : "ບັນທຶກໝາຍເຫດ"}
                      </Button>
                    </CardContent>
                  </Card>
                </div>
              </div>
            </div>
          )}
        </QueryState>
      </Main>

      {booking && (
        <Dialog open={checkInOpen} onOpenChange={setCheckInOpen}>
          <DialogPortal>
            <DialogContent className="max-w-2xl">
              <DialogTitle>
                {isCompletedInstructorCheckIn
                  ? "ກວດຊື່ຄູສອນ"
                  : "ກວດຊື່ການຈອງ"}
              </DialogTitle>
              <CheckInPrivateForm
                bookingId={booking.id}
                studentName={booking.studentName}
                studentMemberId={booking.studentMemberId}
                packageKind={booking.packageKind}
                instructorIds={booking.instructorIds ?? []}
                instructorNames={booking.instructorNames ?? []}
                onSuccess={() => {
                  setCheckInOpen(false);
                  result.refetch();
                }}
                onCheckInComplete={() => {
                  setCheckInOpen(false);
                  result.refetch();
                }}
                onCancel={() => setCheckInOpen(false)}
              />
            </DialogContent>
          </DialogPortal>
        </Dialog>
      )}

      <EditCheckInTimeDialog
        open={editTimeOpen}
        onOpenChange={setEditTimeOpen}
        target={editTimeTarget}
        onConfirm={async (values) => {
          await patchTimeMutation.mutateAsync({
            recordId: values.id,
            checkedInAt: values.checkedInAt,
          });
          setEditTimeOpen(false);
          setEditTimeTarget(null);
        }}
        isSubmitting={patchTimeMutation.isPending}
      />
    </>
  );
}
